// src/app/modules/.../ServiceRequests.tsx
import React, {FC, useEffect, useMemo, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import {Loading} from '../../../../components/Loading/Loading'
import {ServiceRequestModal} from '../../../../components/ServiceResquests/Modal'
import styles from './serviceRequests.module.css'
import {Input} from '../../../../components/Input/Input'
import {IconPickerModal} from './components/IconPickerModal'
import useModals from '../../../../hooks/useModals'
import {GET, POST} from '../../../../services/api'
import SVG from 'react-inlinesvg'
import {EditorQuill} from '../../../../components/EditorQuill/EditorQuill'
import {i18n} from '../../../../translate/i18n'
import Swal from 'sweetalert2'
import {useNavigate} from 'react-router-dom'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'

// DnD-kit
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import uuid from 'react-uuid'

// Catálogo dos “AllForms”
import {
  FORMS_CATALOG,
  FormBlockKind,
  FormBlockMeta,
} from '../../../../components/ServiceResquests/Forms'
import {typeUser} from '../../../Commons/Configuracoes/Usuarios/typeUser'

// ---------------- Tipos/Helpers ----------------
type Block = {id: string; kind: FormBlockKind; gridIds?: number[]; maxQuantity?: number}
const metaByKind = (k: FormBlockKind) => FORMS_CATALOG.find((m) => m.id === k)!

type GridHotel = {
  id: number
  title: string
  status: boolean
  startHour: string
  endHour: string
  interval: number
}

// Normaliza SVG vindo da API (remove width/height/fill/stroke inline)
const normalizeSvg = (svg: string) =>
  svg?.replace(/(width|height)="[^"]*"/gi, '')?.replace(/\s*(fill|stroke)="[^"]*"/gi, '') ?? ''

// Item sortável (bloco no canvas)
function SortableBlock({
  block,
  meta,
  onRemove,
  onDetails, // ADIÇÃO
  onOpenGridModal,
  onOpenQuantityModal,
}: {
  block: Block
  meta: FormBlockMeta
  onRemove: (id: string) => void
  onDetails: (id: string) => void
  onOpenGridModal: (blockId: string) => void
  onOpenQuantityModal: (blockId: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: block.id})

  return (
    <div
      ref={setNodeRef}
      className={`${styles.item} ${isDragging ? styles.dragging : ''}`}
      style={{transform: CSS.Transform.toString(transform), transition}}
      aria-roledescription='sortable item'
    >
      <div className={styles.header}>
        {/* HANDLE — só ele arrasta */}
        <button
          type='button'
          className={styles.handle}
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          aria-label={i18n.t('service_requests.drag_title')}
          title={i18n.t('service_requests.drag_action')}
        >
          <i className='bi bi-grip-vertical' />
        </button>

        {meta.icon && <i className={`${meta.icon}`} />}
        <span className={styles.title}>
          {meta.title}
          {block.gridIds && block.gridIds.length > 0 && (
            <span
              className='badge badge-primary ms-2'
              title={`${i18n.t('service_requests.grid_services_selected')}: ${block.gridIds.join(
                ', '
              )}`}
            >
              {block.gridIds.length}{' '}
              {block.gridIds.length > 1
                ? i18n.t('service_requests.grids_plural')
                : i18n.t('service_requests.grid')}
            </span>
          )}
          {block.maxQuantity && block.maxQuantity > 1 && (
            <span
              className='badge badge-success ms-2'
              title={`${i18n.t('service_requests.max_quantity_title')} ${block.maxQuantity}`}
            >
              {i18n.t('service_requests.max_prefix')} {block.maxQuantity}
            </span>
          )}
        </span>

        <span className={styles.spacer} />

        {/* Botão de Quantidade - aparece para "quantity", "selectListQuantity" e "selectCount" */}
        {(block.kind === 'quantity' ||
          block.kind === 'selectListQuantity' ||
          block.kind === 'selectCount') && (
          <button
            type='button'
            className={styles.iconBtn}
            onClick={() => onOpenQuantityModal(block.id)}
            title={i18n.t('service_requests.max_quantity_modal_title')}
            aria-label={i18n.t('service_requests.max_quantity_modal_title')}
          >
            <i className='bi bi-123' />
          </button>
        )}

        {/* Botão de Grades de Serviços - aparece apenas para tipos "select" */}
        {block.kind.startsWith('select') && (
          <button
            type='button'
            className={styles.iconBtn}
            onClick={() => onOpenGridModal(block.id)}
            title={i18n.t('service_requests.grid_services')}
            aria-label={i18n.t('service_requests.grid_services')}
          >
            <i className='bi bi-grid' />
          </button>
        )}

        <button
          type='button'
          className={styles.iconBtn}
          onClick={() => onRemove(block.id)}
          title={i18n.t('service_requests.remove_title')}
          aria-label={i18n.t('service_requests.remove_aria_label')}
        >
          <i className='bi bi-trash text-danger' />
        </button>
      </div>

      {/* Preview só visual e compacto */}
      <div className={styles.body}>{meta.getPreview()}</div>
    </div>
  )
}

// ---------------- Página ----------------
const Content: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [openOptionsMenu, setOpenOptionsMenu] = useState(false)

  const [blocks, setBlocks] = useState<Block[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  // Campos da sessão (topo)
  const [sessionTitle, setSessionTitle] = useState<string>('') // título
  const [sessionIcon, setSessionIcon] = useState<string>('') // classe bi-* (fallback)
  const [sessionIconId, setSessionIconId] = useState<number | null>(null) // id do ícone escolhido
  const [sessionIconSvg, setSessionIconSvg] = useState<string>('') // svg do ícone escolhido
  const [status, setStatus] = useState<boolean>(true) // ativo/inativo
  const [priority, setPriority] = useState<string>('normal')
  const [role, setRole] = useState<string>('')
  const [visibleTo, setVisibleTo] = useState<string>('both') // visível para: employees, customers, both

  const [description, setDescription] = useState<string>('')

  // ModalWrapper (contexto)
  const {showWrapperModal} = useModals()

  // Picker de ícones
  const [icons, setIcons] = useState<any[]>([])
  const [iconsQuery, setIconsQuery] = useState('')
  const [selectedIcon, setSelectedIcon] = useState<string>('') // classe selecionada (se existir)
  const [selectedIconId, setSelectedIconId] = useState<number | null>(null) // id selecionado no modal

  // Estados para modal de grades de serviços
  const [openGridModal, setOpenGridModal] = useState(false)
  const [currentBlockId, setCurrentBlockId] = useState<string | null>(null)
  const [gridHotels, setGridHotels] = useState<GridHotel[]>([])
  const [loadingGrids, setLoadingGrids] = useState(false)
  const [gridSearchQuery, setGridSearchQuery] = useState('')
  const [selectedGridIds, setSelectedGridIds] = useState<number[]>([])
  const [isGridModalMinimized, setIsGridModalMinimized] = useState(false)

  // Estados para modal de quantidade
  const [openQuantityModal, setOpenQuantityModal] = useState(false)
  const [currentQuantityBlockId, setCurrentQuantityBlockId] = useState<string | null>(null)
  const [maxQuantity, setMaxQuantity] = useState<number>(1)

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 6}}))

  const addBlock = (kind: FormBlockKind) => setBlocks((prev) => [...prev, {id: uuid(), kind}])
  const removeBlock = (id: string) => setBlocks((prev) => prev.filter((b) => b.id !== id))

  // Função para abrir modal de grades de serviços
  const openGridModalForBlock = (blockId: string) => {
    setCurrentBlockId(blockId)
    setOpenGridModal(true)
    setIsGridModalMinimized(false)
    setGridSearchQuery('')

    // Carregar grades já selecionadas para este bloco
    const currentBlock = blocks.find((b) => b.id === blockId)
    setSelectedGridIds(currentBlock?.gridIds || [])

    fetchGridHotels()
  }

  // Função para buscar grades de hotel
  const fetchGridHotels = async () => {
    try {
      if (gridHotels.length !== 0) return
      setLoadingGrids(true)
      const res = await GET('/grid-hotel')
      setGridHotels(res.data || [])
    } catch (error) {
      console.error(i18n.t('service_requests.grid_services_error'), error)
      setGridHotels([])
    } finally {
      setLoadingGrids(false)
    }
  }

  // Função para togglear seleção de uma grade
  const toggleGridSelection = (gridId: number) => {
    setSelectedGridIds((prev) => {
      if (prev.includes(gridId)) {
        return prev.filter((id) => id !== gridId)
      } else {
        return [...prev, gridId]
      }
    })
  }

  // Função para confirmar seleção de grades
  const confirmGridSelection = () => {
    if (currentBlockId) {
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === currentBlockId ? {...block, gridIds: selectedGridIds} : block
        )
      )
    }
    setOpenGridModal(false)
    setCurrentBlockId(null)
    setSelectedGridIds([])
    setGridSearchQuery('')
  }

  // Função para abrir modal de quantidade
  const openQuantityModalForBlock = (blockId: string) => {
    setCurrentQuantityBlockId(blockId)
    const currentBlock = blocks.find((b) => b.id === blockId)
    setMaxQuantity(currentBlock?.maxQuantity || 1)
    setOpenQuantityModal(true)
  }

  // Função para confirmar quantidade máxima
  const confirmMaxQuantity = () => {
    if (currentQuantityBlockId) {
      setBlocks((prev) =>
        prev.map((block) => (block.id === currentQuantityBlockId ? {...block, maxQuantity} : block))
      )
    }
    setOpenQuantityModal(false)
    setCurrentQuantityBlockId(null)
    setMaxQuantity(1)
  }

  const onDragEnd = ({active, over}: DragEndEvent) => {
    setActiveId(null)
    if (!over || active.id === over.id) return
    setBlocks((prev) => {
      const oldIndex = prev.findIndex((b) => b.id === active.id)
      const newIndex = prev.findIndex((b) => b.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const activeBlock = activeId ? blocks.find((b) => b.id === activeId) : null

  // Filtrar grades por busca
  const filteredGrids = useMemo(() => {
    if (!gridSearchQuery) return gridHotels
    const query = gridSearchQuery.toLowerCase()
    return gridHotels.filter((grid) => grid.title.toLowerCase().includes(query))
  }, [gridHotels, gridSearchQuery])

  // Abre o modal de ícones
  const openIconPicker = async () => {
    setSelectedIcon(sessionIcon) // pré-seleciona classe atual
    setSelectedIconId(sessionIconId) // pré-seleciona id atual (pra marcar ativo)
    showWrapperModal(String(i18n.t('service_requests.choose_icon')))
  }

  const ensureDescription = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const text = doc.body.textContent?.trim() || ''

    return text === '' ? '' : html.trim()
  }

  // Função para salvar o formulário de serviço
  const handleSave = async () => {
    try {
      setLoading(true)

      // Validações básicas
      if (!sessionTitle.trim()) {
        throw new Error(String(i18n.t('service_requests.validation_title')))
      }

      if (sessionIconId === null) {
        throw new Error(String(i18n.t('service_requests.validation_icon')))
      }

      if (blocks.length === 0) {
        throw new Error(String(i18n.t('service_requests.validation_components')))
      }

      if (!role) {
        throw new Error('O campo Role é obrigatório')
      }

      // Validação específica para componentes do tipo "select" - devem ter grades selecionadas
      const selectBlocksWithoutGrids = blocks.filter(
        (block) => block.kind.startsWith('select') && (!block.gridIds || block.gridIds.length === 0)
      )

      if (selectBlocksWithoutGrids.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('service_requests.validation_grids_required')),
          text: String(i18n.t('service_requests.validation_grids_text')),
          confirmButtonText: 'OK',
        })
        return
      }

      const payload = {
        title: sessionTitle.trim(),
        description: ensureDescription(description),
        iconId: sessionIconId ?? undefined,
        forms: blocks.map(({id, kind, gridIds, maxQuantity}, index) => ({
          typeRoomService: `${kind}`,
          ...(gridIds && gridIds.length > 0 && {gridHotelIds: gridIds}),
          maxQuantity: maxQuantity && maxQuantity >= 1 ? maxQuantity : 1,
          order: index,
        })),
        status,
        priority,
        role,
        visibleTo,
      }

      const res = await POST('/room-service', payload)

      // Simular delay da API
      // await new Promise((resolve) => setTimeout(resolve, 1500))

      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('general.success')),
          text: String(i18n.t('service_requests.success_created')),
        }).then(() => navigate('/hospitality/structured-content/service-requests'))
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('general.error')),
          text: res?.message || String(i18n.t('service_requests.error_create')),
        })
      }
    } catch (error: any) {
      console.error('Erro ao salvar formulário de serviço:', error)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('general.error')),
        text: error?.message || String(i18n.t('service_requests.error_create')),
      })
    } finally {
      setLoading(false)
    }
  }

  const init = async () => {
    try {
      setLoading(true)
      const res = await POST('/svgwebapp/filter')
      setIcons(res?.data ?? [])
    } catch (error) {
      console.error(error)
      setIcons([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('service_requests.create')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('service_requests.visual_instructions')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('service_requests.add_component')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={() => setOpenOptionsMenu(true)}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>

            <div className='card-body'>
              {/* ===== Topo: Título e Ícone (preview) ===== */}
              <div>
                <div className='row g-3 align-items-end'>
                  <div className='col-md-6'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.form_title')}
                    </div>
                    <Input
                      inputAttr={{type: 'default'}}
                      label=''
                      value={sessionTitle}
                      change={(v: string) => setSessionTitle(v)}
                      placeholder={String(i18n.t('service_requests.placeholder_title'))}
                      maxCaracteres={30}
                    />
                  </div>

                  <div className='col-md-6'>
                    <div className={styles.configLabel}>{i18n.t('service_requests.form_icon')}</div>
                    <div className={styles.iconField}>
                      {/* PREVIEW DO ÍCONE SELECIONADO */}
                      <div
                        className={styles.iconPreview}
                        title={
                          sessionIconSvg
                            ? String(i18n.t('service_requests.icon_selected'))
                            : sessionIcon || String(i18n.t('service_requests.icon_none'))
                        }
                      >
                        {sessionIconSvg ? (
                          <SVG src={sessionIconSvg} style={{width: 22, height: 22}} />
                        ) : (
                          <i className={`bi ${sessionIcon || 'bi-image'}`} />
                        )}
                      </div>

                      <div className={styles.iconInputRow}>
                        <button
                          type='button'
                          className='btn btn-sm btn-light chooseBtn'
                          onClick={openIconPicker}
                        >
                          {i18n.t('service_requests.choose_icon')}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-2'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.form_status')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={status}
                      change={(val: boolean) => setStatus(val)}
                      // label={""}
                      options={[
                        {select: i18n.t(`general.active`), value: true},
                        {select: i18n.t(`general.inactive`), value: false},
                      ]}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-4'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.priority_label')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={priority}
                      change={(val: string) => setPriority(val)}
                      options={[
                        {select: i18n.t('service_requests.priority_low'), value: 'low'},
                        {select: i18n.t('service_requests.priority_normal'), value: 'normal'},
                        {select: i18n.t('service_requests.priority_high'), value: 'high'},
                        {select: i18n.t('service_requests.priority_critical'), value: 'critical'},
                      ]}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-6'>
                    <div className={styles.configLabel}>Role do Serviço</div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={role}
                      change={(val: string) => setRole(val)}
                      options={[{select: 'Nenhum', value: ''}, ...typeUser('hospitality')]}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-6'>
                    <div className={styles.configLabel}>
                      {i18n.t('service_requests.visible_to_label')}
                    </div>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={visibleTo}
                      change={(val: string) => setVisibleTo(val)}
                      options={[
                        {
                          select: i18n.t('service_requests.visible_to_employees'),
                          value: 'employees',
                        },
                        {
                          select: i18n.t('service_requests.visible_to_customers'),
                          value: 'customers',
                        },
                        {select: i18n.t('service_requests.visible_to_both'), value: 'both'},
                      ]}
                      marginTop='0'
                    />
                  </div>
                  <div>
                    <div>
                      <div className={styles.configLabel}>
                        {i18n.t('service_requests.description_label')}
                      </div>
                    </div>
                    <div className='mt-6'>
                      <EditorQuill
                        change={(value: string) => {
                          setDescription(value)
                        }}
                        value={description}
                        maxLength={100}
                        height={150}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== Divisor das seções ===== */}
              <div className={styles.split}>
                <span className={styles.splitTitle}>
                  {i18n.t('service_requests.sections_label')}
                </span>
                {/* <div className={styles.splitLine} /> */}
              </div>

              {/* ===== Canvas / DnD ===== */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(e) => setActiveId(String(e.active.id))}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={styles.list}>
                    <div className='d-grid gap-3'>
                      {blocks.length === 0 ? (
                        <div className={styles.empty}>
                          {i18n.t('service_requests.empty_message')}
                        </div>
                      ) : (
                        blocks.map((b) => (
                          <SortableBlock
                            key={b.id}
                            block={b}
                            meta={metaByKind(b.kind)}
                            onRemove={removeBlock}
                            onDetails={(id) => {}}
                            onOpenGridModal={openGridModalForBlock}
                            onOpenQuantityModal={openQuantityModalForBlock}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeBlock ? (
                    <div className={styles.overlay} style={{width: 640, maxWidth: '90vw'}}>
                      <SortableBlock
                        block={activeBlock}
                        meta={metaByKind(activeBlock.kind)}
                        onRemove={() => {}}
                        onDetails={() => {}}
                        onOpenGridModal={() => {}}
                        onOpenQuantityModal={() => {}}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>

            {/* Botões fixos no bottom da página */}
            <div className='card-footer border-0 pt-0 d-flex justify-content-between'>
              <Button
                text={i18n.t(`buttons.back`)}
                color='info'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => navigate('/hospitality/structured-content/service-requests')}
                btnClass='fw-bolder'
              />
              <Button
                text={i18n.t(`buttons.save`)}
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                disabled={saving}
                btnClass='fw-bolder'
              />
            </div>
          </div>

          {/* Modal “Adicionar componente” (catálogo) */}
          {openOptionsMenu && (
            <ServiceRequestModal
              onCloseAction={() => setOpenOptionsMenu(false)}
              title={i18n.t('service_requests.add_component_modal')}
              showAlert={false}
            >
              <div className='pt-6 d-grid gap-2'>
                {FORMS_CATALOG.map((meta) => (
                  <button
                    key={meta.id}
                    className='btn btn-sm btn-light d-flex align-items-center text-start'
                    onClick={() => {
                      addBlock(meta.id)
                      setOpenOptionsMenu(false)
                    }}
                  >
                    {meta.icon && <i className={`${meta.icon} me-2`} />}
                    {meta.title}
                  </button>
                ))}
              </div>
            </ServiceRequestModal>
          )}

          {/* ModalWrapper: Picker de Ícone (shared component) */}
          <IconPickerModal
            isOpen={false}
            wrapperModalOpen={true}
            icons={icons}
            selectedIconId={selectedIconId}
            selectedIcon={selectedIcon}
            setSelectedIcon={setSelectedIcon}
            setSelectedIconId={setSelectedIconId}
            onClose={() => {
              setIconsQuery('')
              setSelectedIcon('')
              setSelectedIconId(null)
            }}
            onConfirm={() => {
              if (selectedIconId != null) {
                const chosen = icons.find((i: any) => i.id === selectedIconId)
                if (chosen?.svg) setSessionIconSvg(chosen.svg)
                setSessionIconId(selectedIconId)
              } else {
                setSessionIconSvg('')
                setSessionIconId(null)
              }
              setSessionIcon(selectedIcon || '')
            }}
          />

          {/* Modal de Grades de Serviços */}
          {openGridModal && (
            <ServiceRequestModal
              onCloseAction={() => {
                setOpenGridModal(false)
                setCurrentBlockId(null)
                setSelectedGridIds([])
                setGridSearchQuery('')
              }}
              title={i18n.t('service_requests.grid_services_title')}
            >
              <div>
                {/* Cabeçalho com busca */}
                <div className='mb-4'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('service_requests.grid_services_search')}
                    value={gridSearchQuery}
                    change={(v: string) => setGridSearchQuery(v)}
                    placeholder={i18n.t('service_requests.search_placeholder')}
                  />

                  {/* Contador minimalista */}
                  <div className='d-flex justify-content-between align-items-center mt-3 pt-3'>
                    <span className='text-muted small'>
                      {i18n.t('service_requests.grid_services_label')}
                    </span>
                    <div className='d-flex align-items-center gap-2'>
                      <span className='text-muted small'>
                        {i18n.t('service_requests.grid_services_selected_label')}
                      </span>
                      <span className='badge badge-light-primary fs-7'>
                        {selectedGridIds.length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Lista de grades */}
                <div className='mb-4 pb-4 border-bottom'>
                  {loadingGrids ? (
                    <div className='text-center py-5'>
                      <Loading />
                    </div>
                  ) : filteredGrids.length === 0 ? (
                    <div className='text-center text-muted py-5'>
                      {gridSearchQuery
                        ? i18n.t('service_requests.no_grid_found')
                        : i18n.t('service_requests.no_grid_available')}
                    </div>
                  ) : (
                    <div className='d-grid gap-4'>
                      {filteredGrids.map((grid) => (
                        <div
                          key={grid.id}
                          className={`border rounded p-3 ${
                            selectedGridIds.includes(grid.id)
                              ? 'border-primary bg-light-primary'
                              : 'border-light'
                          }`}
                          onClick={() => toggleGridSelection(grid.id)}
                          style={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: selectedGridIds.includes(grid.id)
                              ? undefined
                              : '#f5f8fa',
                          }}
                        >
                          <div className='d-flex align-items-center justify-content-between'>
                            <div className='flex-grow-1 d-flex align-items-center justify-content-between py-1'>
                              <h6 className='mb-1'>{grid.title}</h6>
                              <span
                                className={`badge ${
                                  grid.status ? 'badge-success' : 'badge-danger'
                                }`}
                              >
                                {grid.status
                                  ? i18n.t('service_requests.grid_status_active')
                                  : i18n.t('service_requests.grid_status_inactive')}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Botões de ação */}
                <div className='d-flex justify-content-end gap-2'>
                  <Button
                    text={i18n.t('service_requests.cancel_button')}
                    color='danger'
                    size='small'
                    btnAttr={{type: 'button'}}
                    click={() => {
                      setOpenGridModal(false)
                      setCurrentBlockId(null)
                      setSelectedGridIds([])
                      setGridSearchQuery('')
                    }}
                  />
                  <Button
                    text={i18n.t('service_requests.save_button')}
                    color='primary'
                    size='small'
                    btnAttr={{type: 'button'}}
                    click={confirmGridSelection}
                    disabled={selectedGridIds.length === 0}
                  />
                </div>
              </div>
            </ServiceRequestModal>
          )}

          {/* Modal de Quantidade Máxima */}
          {openQuantityModal && (
            <ServiceRequestModal
              onCloseAction={() => {
                setOpenQuantityModal(false)
                setCurrentQuantityBlockId(null)
                setMaxQuantity(1)
              }}
              title={i18n.t('service_requests.max_quantity_modal_title')}
              width='400px'
            >
              <div>
                <div className='mb-4'>
                  <Input
                    inputAttr={{
                      type: 'number',
                      min: '1',
                      max: '999',
                    }}
                    label={i18n.t('service_requests.max_quantity_label')}
                    value={maxQuantity.toString()}
                    change={(v: string) => setMaxQuantity(parseInt(v) || 1)}
                    placeholder={i18n.t('service_requests.max_quantity_placeholder')}
                  />
                  <div className='form-text text-muted mt-2'>
                    {i18n.t('service_requests.max_quantity_helper')}
                  </div>
                </div>

                {/* Botões de ação */}
                <div className='d-flex justify-content-end gap-2'>
                  <Button
                    text={i18n.t('service_requests.cancel_button')}
                    color='secondary'
                    size='small'
                    btnAttr={{type: 'button'}}
                    click={() => {
                      setOpenQuantityModal(false)
                      setCurrentQuantityBlockId(null)
                      setMaxQuantity(1)
                    }}
                  />
                  <Button
                    text={i18n.t('service_requests.confirm_button')}
                    color='primary'
                    size='small'
                    btnAttr={{type: 'button'}}
                    click={confirmMaxQuantity}
                    disabled={maxQuantity < 1}
                  />
                </div>
              </div>
            </ServiceRequestModal>
          )}
        </div>
      )}
    </>
  )
}

const ServiceRequestsCreate: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('service_requests.breadcrumb_dashboard'),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('service_requests.breadcrumb_essential_services'),
            path: 'hospitality',
            isActive: false,
          },
          {
            title: i18n.t('service_requests.title') as string,
            path: 'service-requests',
            isActive: false,
          },
          {title: i18n.t('service_requests.breadcrumb_create'), path: 'create', isActive: true},
        ]}
      >
        {i18n.t('service_requests.create')}
      </PageTitle>
      <Content />
    </>
  )
}

export {ServiceRequestsCreate}
